import type { SVGProps } from 'react';
import { cn } from '../lib/cn';

/*
 * The PLANETRA logo (P1-MVP-04), from the Brand DNA's own artwork
 * (PLANETRA Landing page/assets/logo-lockup.svg), not redrawn: the Brand DNA
 * asks for one master shape, preserved (§8).
 *
 * The mark is mint on any ground, as the Brand DNA prefers on charcoal. The
 * wordmark takes `currentColor`, so it is off-white on the product's charcoal
 * and follows whatever text colour surrounds it elsewhere. The lockup's
 * tagline is left out: at header size it is unreadable, and the Brand DNA
 * keeps "Digitize. Discover. Decide." for places it can be read. Without the
 * tagline beneath it the wordmark is re-centred on the mark (y 97 → 138), as
 * the Brand DNA's own interface concepts show it.
 *
 * The Brand DNA calls the lettering concept artwork; when the final vector
 * lands, these two path strings are what change.
 */

const MARK =
  'M268 293 167 350 72 294 6 334 166 431 268 371ZM396 179 325 148 167 242 70 190 7 230 166 318ZM397 71 245 6 7 135 166 210Z';

const WORDMARK =
  'M1429 126 1430 128 1490 128 1489 123 1477 101 1472 99 1443 100ZM448 127 508 127 494 100 491 99 462 100ZM1355 151 1355 153 1402 153 1459 50 1469 66 1473 76 1515 153 1565 153 1485 6 1434 7ZM1162 6 1162 43 1287 43 1292 45 1297 53 1296 61 1287 68 1162 68 1162 153 1206 153 1206 108 1209 106 1246 107 1286 153 1340 153 1298 106 1299 104 1308 103 1323 96 1331 88 1337 77 1340 64 1340 44 1337 32 1326 16 1315 9 1305 6ZM977 6 976 43 1032 43 1035 46 1035 153 1079 153 1079 47 1082 43 1140 43 1140 6ZM802 6 802 154 958 153 958 116 848 116 846 114 847 95 942 95 943 64 848 64 846 62 846 45 853 43 958 43 958 6ZM594 7 595 153 638 152 639 65 728 153 776 153 776 6 732 6 732 78 730 94 641 6ZM376 152 422 153 473 57 478 51 488 67 533 153 581 153 566 122 503 6 454 7ZM217 6 216 152 362 153 362 113 261 112 261 8 260 6ZM6 151 7 153 50 153 52 107 155 106 163 104 176 97 186 86 193 67 192 38 184 21 173 11 159 6 7 6 7 43 140 43 148 49 149 60 144 66 139 68 7 68Z';

export interface LogoProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** The accessible name — the product's name, from the caller's locale. */
  label: string;
  /** `lockup`: mark and wordmark. `mark`: the stacked layers alone. */
  variant?: 'lockup' | 'mark';
}

export function Logo({ label, variant = 'lockup', className, ...props }: LogoProps) {
  const lockup = variant === 'lockup';

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={lockup ? '0 0 2051 438' : '0 0 405 438'}
      className={cn('h-6 w-auto shrink-0', className)}
      {...props}
    >
      <path fill="var(--pl-c-mint)" fillRule="evenodd" d={MARK} />
      {lockup ? (
        <path fill="currentColor" fillRule="evenodd" transform="translate(479 138)" d={WORDMARK} />
      ) : null}
    </svg>
  );
}
